import {
  Box,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  HStack,
  Select,
  VStack,
  Spinner,
  Center,
  Icon,
  Button,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Tag,
  Tooltip,
  IconButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  NumberInput,
  NumberInputField,
  Portal,
  useToast,
} from '@chakra-ui/react'
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { fr } from 'date-fns/locale'
import { LuUsers, LuActivity, LuChevronDown, LuChevronUp, LuExternalLink, LuClock, LuMessageSquareHeart, LuPlus, LuCheck, LuFilter, LuDownload } from 'react-icons/lu'
import { BsThreeDotsVertical } from 'react-icons/bs'
import { pathologyDisplayName } from '../../components/Lab/BlotAnalysisWorkspace/utils/displayName'
import { downloadSourceImage } from '../../components/Lab/BlotAnalysisWorkspace/utils/sourceImage'
import ActivityDashboard from '../../components/ActivityDashboard'
import { getSavedAuthData } from '../../api/axios/interceptor'
import { CHROME_HEADER_GRADIENT, CHROME_GLASS_BORDER, CHROME_AURORA_VIOLET, CHROME_AURORA_CYAN } from '../../theme/chrome'
import {
  usePilots,
  usePilotDetail,
  usePilotBlotCases,
  useSetPilotCredits,
  useDatasetFacets,
  type PilotSummary,
  type PilotBlotCase,
  type DatasetExportFilters,
} from '../../api/api_hooks/admin/pilots'

/** Human label for the known detection profiles (mirrors BlotDatasetExport). */
const PROFILE_LABEL: Record<string, string> = {
  'chu-nancy': 'CHU Nancy',
  'chu-amiens': 'CHU Amiens',
  'chu-bordeaux': 'CHU Bordeaux',
  'chu-lyon': 'CHU Lyon',
  'chu-grenoble': 'CHU Grenoble',
  aphp: 'AP-HP',
  protis: 'PROTIS',
  default: 'Défaut',
}

// Map a CHU detection profile to keywords found in a pilot's companyName, so
// the CHU filter also narrows the pilots activity table.
const PROFILE_COMPANY_KEYWORDS: Record<string, string[]> = {
  'chu-nancy': ['nancy'],
  'chu-amiens': ['amiens'],
  // Sans cette entrée, choisir « CHU Bordeaux » ne filtrait RIEN : la règle
  // « profil sans mots-clés = pas de restriction » (pensée pour `default`)
  // laissait passer tous les pilotes.
  'chu-bordeaux': ['bordeaux'],
  aphp: ['assistance publique', 'ap-hp', 'ap hp', 'aphp', 'pitie', 'paris'],
  'ap-hp': ['assistance publique', 'ap-hp', 'ap hp', 'aphp', 'pitie', 'paris'],
  'chu-lyon': ['lyon', 'hospices civils'],
  'chu-grenoble': ['grenoble'],
}

function pilotMatchesProfile(companyName: string | null, profile?: string): boolean {
  if (!profile) return true
  const keywords = PROFILE_COMPANY_KEYWORDS[profile]
  if (!keywords || keywords.length === 0) return true // e.g. "default" → no narrowing
  const normalized = (companyName ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
  return keywords.some((k) => normalized.includes(k))
}

const fmtAgo = (iso: string | null) =>
  iso ? formatDistanceToNow(new Date(iso), { addSuffix: true, locale: fr }) : 'jamais'

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'

/**
 * Résumé des corrections manuelles d'un cas : chaque geste compté
 * séparément — « 1 ajout · 3 suppr. · 2 repos. » — plutôt qu'un solde net
 * qui obligeait le lecteur à retrouver les ajouts de tête ; null = la
 * détection a été prise telle quelle, ce qui est aussi une information.
 */
const correctionSummary = (cs: PilotBlotCase): string | null => {
  const placed = Object.values(cs.placedBands ?? {})
    .reduce((n, arr) => n + (Array.isArray(arr) ? arr.length : 0), 0)
  const suppressed = Object.values(cs.suppressedBands ?? {})
    .reduce((n, arr) => n + (Array.isArray(arr) ? arr.length : 0), 0)
  const repositioned = Object.values(cs.repositionedBlots ?? {})
    .reduce((n, c) => n + (Number(c) || 0), 0)
  const parts: string[] = []
  if (placed > 0) parts.push(`${placed} ajout${placed > 1 ? 's' : ''}`)
  if (suppressed > 0) parts.push(`${suppressed} suppr.`)
  if (repositioned > 0) parts.push(`${repositioned} repos.`)
  return parts.length ? parts.join(' · ') : null
}

const planScheme = (plan: string | null) => {
  if (!plan) return 'gray'
  if (plan === 'PREMIUM') return 'purple'
  if (plan === 'ENTERPRISE') return 'orange'
  if (plan === 'PRO') return 'teal'
  return 'gray'
}

interface PilotsDashboardProps {
  /** When true, the gradient hero block is skipped — useful when embedding
   *  inside the existing /admin tab interface that already has its own header. */
  embedded?: boolean
}

/**
 * Pilot follow-up dashboard. Used both as a standalone page (/admin/pilots)
 * and embedded as a tab inside the main /admin Panneau d'Administration.
 *
 * Table of every non-admin user (one row per pilot) with at-a-glance metrics:
 * plan, last activity, blot case counts (total / last 30 days), credits. Click
 * a row to expand and reveal: detailed usage stats + the pilot's recent blot
 * cases (clickable → opens the standard blot workspace in admin-shadow mode).
 *
 * Backs onto /api/users/pilots/usage-summary + /api/users/:id/usage-summary +
 * /api/blot-analysis/recent-cases?userId=… — all admin-bypass routes.
 */
export function PilotsDashboard({ embedded = false }: PilotsDashboardProps) {
  const { data, isLoading, error } = usePilots()
  const { data: facets } = useDatasetFacets()
  const [expandedId, setExpandedId] = useState<string | null>(null)
  // Shared with the export card: the CHU/profile selection also filters the
  // pilots activity table below.
  const [filters, setFilters] = useState<DatasetExportFilters>({})

  const allPilots = data?.pilots ?? []
  const shownPilots = allPilots.filter((p) => pilotMatchesProfile(p.companyName, filters.detectionProfile))
  const isFiltered = !!filters.detectionProfile

  return (
    <Box maxW={embedded ? undefined : '1400px'} mx="auto" p={embedded ? 0 : { base: 3, md: 6 }}>
      {/* Hero header — skipped in embedded mode to avoid duplicating the admin header */}
      {!embedded && (
      <Box
        mb={6}
        p={6}
        borderRadius="2xl"
        bgGradient={CHROME_HEADER_GRADIENT}
        color="white"
        position="relative"
        overflow="hidden"
        boxShadow="0 20px 50px -12px rgba(15, 10, 26, 0.5)"
        sx={{ '&::after': CHROME_GLASS_BORDER }}
      >
        <Box position="absolute" top="-50px" right="-30px" w="200px" h="200px" borderRadius="full"
          bg={CHROME_AURORA_VIOLET} pointerEvents="none" />
        <Box position="absolute" bottom="-70px" left="20%" w="240px" h="240px" borderRadius="full"
          bg={CHROME_AURORA_CYAN} pointerEvents="none" />
        <HStack spacing={4} position="relative" zIndex={1}>
          <Box w="56px" h="56px" borderRadius="xl" bg="whiteAlpha.200" display="grid" sx={{ placeItems: 'center' }}>
            <Icon as={LuUsers} boxSize={7} />
          </Box>
          <VStack align="start" spacing={0}>
            <Heading size="lg" fontWeight="800" letterSpacing="tight">Suivi des pilotes</Heading>
            <Text fontSize="sm" color="whiteAlpha.800">
              {data?.count != null
                ? isFiltered
                  ? `${shownPilots.length} / ${data.count} pilote(s) — filtré`
                  : `${data.count} pilote(s)`
                : 'Chargement…'}{' '}
              — clique sur une ligne pour le détail
            </Text>
          </VStack>
        </HStack>
      </Box>
      )}

      {/* CHU filter — kept here after the dataset export moved to its own tab,
          so narrowing the activity table by site doesn't require leaving it. */}
      <HStack mb={5} spacing={3}>
        <Icon as={LuFilter} boxSize={4} color="gray.400" />
        <Select
          size="sm"
          maxW="260px"
          bg="white"
          placeholder="Tous les CHU"
          value={filters.detectionProfile ?? ''}
          onChange={(e) =>
            setFilters({ ...filters, detectionProfile: e.target.value || undefined })
          }
        >
          {facets?.profiles.map((p) => (
            <option key={p.profile} value={p.profile}>
              {PROFILE_LABEL[p.profile] ?? p.profile} ({p.count})
            </option>
          ))}
        </Select>
      </HStack>

      {isLoading && (
        <Center py={20}>
          <Spinner size="xl" color="purple.500" thickness="4px" />
        </Center>
      )}

      {error && (
        <Center py={20}>
          <Text color="red.500">Erreur de chargement : {(error as Error).message}</Text>
        </Center>
      )}

      {data && data.count === 0 && (
        <Center py={20} bg="gray.50" borderRadius="xl" border="1px dashed" borderColor="gray.200">
          <VStack spacing={2}>
            <Icon as={LuUsers} boxSize={10} color="gray.400" />
            <Text color="gray.600">Aucun pilote inscrit pour le moment.</Text>
          </VStack>
        </Center>
      )}

      {data && data.count > 0 && shownPilots.length === 0 && (
        <Center py={16} bg="gray.50" borderRadius="xl" border="1px dashed" borderColor="gray.200">
          <VStack spacing={2}>
            <Icon as={LuUsers} boxSize={9} color="gray.400" />
            <Text color="gray.600">Aucun pilote ne correspond au filtre CHU sélectionné.</Text>
            <Button size="xs" variant="outline" onClick={() => setFilters({ ...filters, detectionProfile: undefined })}>
              Réinitialiser le filtre
            </Button>
          </VStack>
        </Center>
      )}

      {data && data.count > 0 && shownPilots.length > 0 && (
        <Box bg="white" borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.100" overflowX="auto">
          <Table size="sm" variant="simple" minW="920px">
            <Thead bg="gray.50">
              <Tr>
                <Th></Th>
                <Th>Pilote</Th>
                <Th>CHU</Th>
                <Th>Plan</Th>
                <Th>Dernière activité</Th>
                <Th isNumeric>Cas IRIS</Th>
                <Th isNumeric>Cas 30 j</Th>
                <Th isNumeric>Blots</Th>
                <Th isNumeric>Crédits restants</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {shownPilots.map((p) => {
                const isOpen = expandedId === p.id
                return (
                  <PilotRow
                    key={p.id}
                    pilot={p}
                    isOpen={isOpen}
                    onToggle={() => setExpandedId(isOpen ? null : p.id)}
                  />
                )
              })}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Pilot feedback — responses to the in-app satisfaction survey */}
      <FeedbackSection />
    </Box>
  )
}

const TIME_SAVING_LABEL: Record<string, string> = {
  beaucoup: 'Beaucoup',
  un_peu: 'Un peu',
  pas_vraiment: 'Pas vraiment',
}

/** Admin-only list of in-app feedback responses, newest first, with the average NPS. */
function FeedbackSection() {
  const [rows, setRows] = useState<any[] | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    const { accessToken } = getSavedAuthData()
    fetch(`${process.env.REACT_APP_API_DOMAIN || ''}/api/feedback`, {
      headers: { Authorization: accessToken || '' },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d) => setRows(d.feedbacks ?? []))
      .catch(() => setFailed(true))
  }, [])

  // Hidden on error (e.g. 403) or until loaded. Empty is shown so admins know the
  // survey is live but nobody has answered yet.
  if (failed || rows === null) return null

  const nps = rows.map((r) => r.npsScore).filter((n: any) => typeof n === 'number')
  const avgNps = nps.length ? (nps.reduce((a: number, b: number) => a + b, 0) / nps.length).toFixed(1) : '—'

  return (
    <Box mt={8} bg="white" borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.100" overflowX="auto">
      <HStack px={5} py={4} borderBottom="1px solid" borderColor="gray.100" justify="space-between">
        <HStack spacing={2}>
          <Icon as={LuMessageSquareHeart} color="teal.500" boxSize={5} />
          <Heading size="sm">Avis des pilotes</Heading>
          <Badge colorScheme="gray" borderRadius="full">{rows.length}</Badge>
        </HStack>
        {rows.length > 0 && (
          <Text fontSize="sm" color="gray.600">NPS moyen : <Text as="span" fontWeight="bold" color="teal.600">{avgNps}</Text> / 10</Text>
        )}
      </HStack>
      {rows.length === 0 ? (
        <Center py={10}>
          <Text color="gray.500" fontSize="sm">Aucun avis pour le moment.</Text>
        </Center>
      ) : (
        <Table size="sm" variant="simple" minW="720px">
          <Thead bg="gray.50">
            <Tr>
              <Th>Pilote</Th>
              <Th isNumeric>NPS</Th>
              <Th isNumeric>Confiance</Th>
              <Th>Gain de temps</Th>
              <Th>Commentaire</Th>
              <Th>Date</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((r) => (
              <Tr key={r.id}>
                <Td>
                  <VStack align="start" spacing={0}>
                    <Text fontWeight="600" fontSize="sm">{r.user?.contactName ?? r.user?.email ?? 'Utilisateur supprimé'}</Text>
                    <Text fontSize="xs" color="gray.500">{r.user?.companyName ?? '—'}</Text>
                  </VStack>
                </Td>
                <Td isNumeric fontWeight="700" color={r.npsScore == null ? 'gray.400' : r.npsScore >= 9 ? 'green.600' : r.npsScore >= 7 ? 'orange.500' : 'red.500'}>
                  {r.npsScore ?? '—'}
                </Td>
                <Td isNumeric>{r.confidence ?? '—'}</Td>
                <Td fontSize="sm">{r.timeSaving ? TIME_SAVING_LABEL[r.timeSaving] ?? r.timeSaving : '—'}</Td>
                <Td fontSize="xs" color="gray.700" maxW="320px" whiteSpace="pre-wrap">{r.comment || '—'}</Td>
                <Td fontSize="xs" color="gray.500" whiteSpace="nowrap">{fmtAgo(r.createdAt)}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
    </Box>
  )
}

// Default export = the standalone /admin/pilots page (PilotsDashboard with its own hero).
export default function PilotsAdmin() {
  return <PilotsDashboard />
}

/* ───────── Row + expanded detail ─────────────────────────────────────── */

/** Remaining credits + an inline popover to GRANT more (admin top-up). */
function CreditsCell({ pilot }: { pilot: PilotSummary }) {
  const remaining = pilot.credits?.remaining ?? null
  const [amount, setAmount] = useState<string>('50')
  const { mutateAsync, isPending } = useSetPilotCredits()
  const toast = useToast()

  const grant = async (n: number, onClose: () => void) => {
    if (!Number.isFinite(n) || n <= 0) {
      toast({ title: 'Montant invalide', status: 'error', duration: 2500 })
      return
    }
    try {
      await mutateAsync({ userId: pilot.id, add: Math.round(n) })
      toast({ title: `+${Math.round(n)} crédits ajoutés`, status: 'success', duration: 2500 })
      onClose()
    } catch {
      toast({ title: "Échec de l'ajout", status: 'error', duration: 3000 })
    }
  }

  return (
    <HStack spacing={1} justify="flex-end" onClick={(e) => e.stopPropagation()}>
      {remaining != null ? (
        <Tooltip
          label={pilot.credits?.shared
            ? "Pot commun de l'équipe — ce collaborateur consomme le solde de son chef"
            : ''}
          isDisabled={!pilot.credits?.shared}
          hasArrow
        >
          <Text color={remaining < 20 ? 'red.500' : 'gray.700'} fontWeight="600">
            {remaining}{pilot.credits?.shared ? ' (partagé)' : ''}
          </Text>
        </Tooltip>
      ) : (
        <Text color="gray.400">—</Text>
      )}
      <Popover placement="left" isLazy>
        {({ onClose }) => (
          <>
            <PopoverTrigger>
              <IconButton
                aria-label="Ajouter des crédits"
                title="Ajouter des crédits"
                icon={<LuPlus />}
                size="xs"
                variant="ghost"
                colorScheme="teal"
              />
            </PopoverTrigger>
            <PopoverContent w="240px">
              <PopoverArrow />
              <PopoverBody>
                <Text fontSize="xs" color="gray.500" mb={2}>
                  Ajouter des crédits {remaining != null ? `(actuel : ${remaining})` : ''}
                </Text>
                <HStack spacing={1} mb={2}>
                  {[50, 100, 200].map((q) => (
                    <Button
                      key={q}
                      size="xs"
                      variant="outline"
                      colorScheme="teal"
                      flex={1}
                      isLoading={isPending}
                      onClick={() => grant(q, onClose)}
                    >
                      +{q}
                    </Button>
                  ))}
                </HStack>
                <HStack>
                  <NumberInput size="sm" value={amount} min={1} onChange={(s) => setAmount(s)}>
                    <NumberInputField placeholder="Montant" />
                  </NumberInput>
                  <IconButton
                    aria-label="Ajouter"
                    icon={<LuCheck />}
                    size="sm"
                    colorScheme="teal"
                    isLoading={isPending}
                    onClick={() => grant(Number(amount), onClose)}
                  />
                </HStack>
              </PopoverBody>
            </PopoverContent>
          </>
        )}
      </Popover>
    </HStack>
  )
}

function PilotRow({ pilot, isOpen, onToggle }: { pilot: PilotSummary; isOpen: boolean; onToggle: () => void }) {
  return (
    <>
      <Tr cursor="pointer" onClick={onToggle} _hover={{ bg: 'gray.50' }}>
        <Td w="40px">
          <Icon as={isOpen ? LuChevronUp : LuChevronDown} color="gray.400" boxSize={4} />
        </Td>
        <Td>
          <VStack align="start" spacing={0}>
            <Text fontWeight="600" fontSize="sm">{pilot.contactName ?? pilot.email}</Text>
            <Text fontSize="xs" color="gray.500">{pilot.email}</Text>
          </VStack>
        </Td>
        <Td fontSize="sm" color="gray.700">{pilot.companyName ?? '—'}</Td>
        <Td>
          <Badge colorScheme={planScheme(pilot.plan)} variant="subtle" borderRadius="full" px={2} fontSize="2xs">
            {pilot.plan ?? 'N/A'}
          </Badge>
        </Td>
        <Td fontSize="xs" color={pilot.lastActivity ? 'gray.700' : 'gray.400'}>
          <HStack spacing={1}>
            <Icon as={LuClock} boxSize={3} />
            <Text>{fmtAgo(pilot.lastActivity)}</Text>
          </HStack>
        </Td>
        <Td isNumeric fontWeight="600">{pilot.blotCases.total}</Td>
        <Td isNumeric color={pilot.blotCases.last30Days > 0 ? 'green.600' : 'gray.400'} fontWeight="600">
          {pilot.blotCases.last30Days}
        </Td>
        <Td isNumeric fontWeight="600" color="gray.700">{pilot.totalBlots ?? '—'}</Td>
        <Td isNumeric fontSize="sm"><CreditsCell pilot={pilot} /></Td>
        <Td w="40px"></Td>
      </Tr>
      {isOpen && (
        <Tr>
          <Td colSpan={10} p={0} borderBottom="none">
            <Box bg="gray.50" px={{ base: 3, md: 6 }} py={5} borderTop="1px solid" borderColor="gray.100" overflowX="auto">
              <PilotDetailPanel userId={pilot.id} />
            </Box>
          </Td>
        </Tr>
      )}
    </>
  )
}

const PILOT_CASES_PAGE_SIZE = 20

function PilotDetailPanel({ userId }: { userId: string }) {
  const { data: detail, isLoading: loadingDetail } = usePilotDetail(userId)
  const [casesLimit, setCasesLimit] = useState(PILOT_CASES_PAGE_SIZE)
  const { data: cases, isLoading: loadingCases, isFetching: fetchingCases } = usePilotBlotCases(userId, casesLimit)
  const navigate = useNavigate()
  const toast = useToast()
  const [downloadingCaseId, setDownloadingCaseId] = useState<string | null>(null)

  // Enregistre le fichier TEL QU'IL A ÉTÉ DÉPOSÉ — un cas envoyé en PDF
  // revient en PDF, pas en image rastérisée. Récupéré plutôt que lié : la
  // route est authentifiée, un simple href reviendrait en 401 et le navigateur
  // enregistrerait la page d'erreur sous le nom du fichier.
  const handleDownloadSource = async (cs: PilotBlotCase) => {
    setDownloadingCaseId(cs.id)
    try {
      const saved = await downloadSourceImage(process.env.REACT_APP_API_DOMAIN || '', cs.fileUrl)
      if (!saved) {
        toast({
          title: 'Fichier indisponible',
          description: "Ce cas n'a pas de fichier d'origine sur le serveur.",
          status: 'warning',
          duration: 4000,
        })
      }
    } catch (error) {
      console.error('Error downloading source image:', error)
      toast({ title: 'Téléchargement impossible', status: 'error', duration: 4000 })
    } finally {
      setDownloadingCaseId(null)
    }
  }

  if (loadingDetail || loadingCases) {
    return <Center py={6}><Spinner color="purple.500" /></Center>
  }

  if (!detail) return null

  const c = detail.credits

  return (
    <VStack align="stretch" spacing={5}>
      {/* KPI strip */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
        <Stat bg="white" p={3} borderRadius="lg" boxShadow="xs">
          <StatLabel fontSize="2xs" color="gray.500" textTransform="uppercase">Cas IRIS</StatLabel>
          <StatNumber fontSize="2xl">{detail.cases.blot.total}</StatNumber>
          <StatHelpText fontSize="xs" mb={0}>{detail.cases.blot.last30Days} sur 30 j</StatHelpText>
        </Stat>
        <Stat bg="white" p={3} borderRadius="lg" boxShadow="xs">
          <StatLabel fontSize="2xs" color="gray.500" textTransform="uppercase">Cas ALMA</StatLabel>
          <StatNumber fontSize="2xl">{detail.cases.malaria.total}</StatNumber>
          <StatHelpText fontSize="xs" mb={0}>{detail.cases.malaria.last30Days} sur 30 j</StatHelpText>
        </Stat>
        <Stat bg="white" p={3} borderRadius="lg" boxShadow="xs">
          <StatLabel fontSize="2xs" color="gray.500" textTransform="uppercase">Crédits IA</StatLabel>
          <StatNumber fontSize="2xl" color={c && c.remaining < 20 ? 'red.500' : 'gray.800'}>
            {c ? c.remaining : '—'}
          </StatNumber>
          <StatHelpText fontSize="xs" mb={0}>
            {c ? `/ ${c.total} · ${c.consumedLast30Days} sur 30 j` : 'Pas de quota'}
          </StatHelpText>
        </Stat>
        <Stat bg="white" p={3} borderRadius="lg" boxShadow="xs">
          <StatLabel fontSize="2xs" color="gray.500" textTransform="uppercase">Modules</StatLabel>
          <StatNumber fontSize="md" fontWeight="600">
            <HStack spacing={1} mt={1} flexWrap="wrap">
              {detail.modules.filter(m => m.enabled).map(m => (
                <Tag key={m.type} size="sm" colorScheme="purple" variant="subtle">{m.type}</Tag>
              ))}
              {detail.modules.filter(m => m.enabled).length === 0 && (
                <Text fontSize="xs" color="gray.400">aucun</Text>
              )}
            </HStack>
          </StatNumber>
        </Stat>
      </SimpleGrid>

      {/* Activité par pathologie — same widget as the home dashboard, scoped
          to this pilot via ?userId= (admin-gated on the backend). */}
      <ActivityDashboard userId={userId} title="Activité du pilote" />

      {/* Recent blot cases — clickable */}
      <Box bg="white" borderRadius="lg" boxShadow="xs" overflow="hidden">
        <HStack justify="space-between" px={4} py={3} borderBottom="1px solid" borderColor="gray.100">
          <HStack spacing={2}>
            <Icon as={LuActivity} color="teal.500" boxSize={4} />
            <Text fontSize="sm" fontWeight="600">
              Derniers cas IRIS ({cases?.count ?? 0})
            </Text>
          </HStack>
          {cases && cases.count > 0 && (
            <Text fontSize="2xs" color="gray.400">
              Clic = ouvrir le cas en lecture (admin shadow)
            </Text>
          )}
        </HStack>
        {cases && cases.count === 0 ? (
          <Center py={6} color="gray.400" fontSize="sm">Aucun cas IRIS pour ce pilote.</Center>
        ) : (
          <Table size="sm" variant="simple" minW="820px">
            <Thead>
              <Tr>
                <Th fontSize="2xs">Sample</Th>
                <Th fontSize="2xs">Fichier</Th>
                <Th fontSize="2xs">Pathologie</Th>
                <Th fontSize="2xs" isNumeric>Blots</Th>
                <Th fontSize="2xs" isNumeric>Bandes</Th>
                <Th fontSize="2xs">
                  <Tooltip hasArrow placement="top" label="Ce que le biologiste a changé après la détection : bandes ajoutées, bandes supprimées, bandes repositionnées. « — » = détection prise telle quelle.">
                    <Text as="span" textDecoration="underline" textDecorationStyle="dotted" cursor="help">Corrections</Text>
                  </Tooltip>
                </Th>
                <Th fontSize="2xs">Validé</Th>
                <Th fontSize="2xs">Modifié</Th>
                <Th fontSize="2xs"></Th>
              </Tr>
            </Thead>
            <Tbody>
              {cases?.analyses.map((cs) => (
                <Tooltip key={cs.id} label="Ouvrir le cas dans le workspace" placement="left" hasArrow openDelay={400}>
                  <Tr
                    cursor="pointer"
                    // Tag the navigation with `from: 'admin-pilots'` so the workspace's
                    // back button returns to /admin/pilots instead of the regular
                    // recent-cases list (which would mix admin's view with the pilot's
                    // case list).
                    onClick={() => navigate(`/lab/blot-analysis/${cs.id}`, { state: { from: 'admin-pilots' } })}
                    _hover={{ bg: 'teal.50' }}
                  >
                    <Td fontFamily="mono" fontSize="xs">{cs.sampleId ?? '—'}</Td>
                    <Td fontSize="xs" maxW="200px"><Text noOfLines={1}>{cs.filename ?? '—'}</Text></Td>
                    <Td fontSize="xs">{pathologyDisplayName(cs.pathologyName ?? '') || '—'}</Td>
                    <Td fontSize="xs" isNumeric>{cs.totalBlots ?? '—'}</Td>
                    <Td fontSize="xs" isNumeric>{cs.totalBands ?? '—'}</Td>
                    <Td fontSize="xs" whiteSpace="nowrap">
                      {correctionSummary(cs) ? (
                        <Text color="purple.700" fontWeight="500">{correctionSummary(cs)}</Text>
                      ) : (
                        <Text color="gray.300">—</Text>
                      )}
                    </Td>
                    <Td fontSize="xs">
                      {cs.validated ? (
                        <Badge colorScheme="green" variant="subtle" borderRadius="full" px={2}>Validé</Badge>
                      ) : (
                        <Badge colorScheme="orange" variant="subtle" borderRadius="full" px={2}>
                          {cs.validatedCount ?? 0}/{cs.sampleTotal ?? 0}
                        </Badge>
                      )}
                    </Td>
                    <Td fontSize="xs" color="gray.500" whiteSpace="nowrap">
                      <VStack align="start" spacing={0}>
                        <Text>{fmtAgo(cs.updatedAt)}</Text>
                        <Text fontSize="2xs" color="gray.400">{fmtDate(cs.updatedAt)}</Text>
                      </VStack>
                    </Td>
                    <Td onClick={(e) => e.stopPropagation()}>
                      <HStack spacing={1} justify="flex-end">
                        <Icon as={LuExternalLink} boxSize={3} color="gray.400" />
                        <Popover placement="bottom-end" isLazy>
                          <PopoverTrigger>
                            <IconButton
                              aria-label="Options du cas"
                              icon={<BsThreeDotsVertical />}
                              size="xs"
                              variant="ghost"
                              color="gray.400"
                              _hover={{ color: 'gray.700', bg: 'gray.100' }}
                            />
                          </PopoverTrigger>
                          <Portal>
                            <PopoverContent w="230px" boxShadow="md">
                              <PopoverArrow />
                              <PopoverBody p={1}>
                                <VStack spacing={0} align="stretch">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    justifyContent="flex-start"
                                    fontWeight={400}
                                    leftIcon={<Icon as={LuExternalLink} />}
                                    onClick={() => navigate(`/lab/blot-analysis/${cs.id}`, { state: { from: 'admin-pilots' } })}
                                  >
                                    Accéder au cas
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    justifyContent="flex-start"
                                    fontWeight={400}
                                    leftIcon={<Icon as={LuDownload} />}
                                    isLoading={downloadingCaseId === cs.id}
                                    loadingText="Téléchargement…"
                                    onClick={() => handleDownloadSource(cs)}
                                  >
                                    Télécharger le fichier d’origine
                                  </Button>
                                </VStack>
                              </PopoverBody>
                            </PopoverContent>
                          </Portal>
                        </Popover>
                      </HStack>
                    </Td>
                  </Tr>
                </Tooltip>
              ))}
            </Tbody>
          </Table>
        )}
        {cases && cases.count >= casesLimit && (
          <Center py={2} borderTop="1px solid" borderColor="gray.100">
            <Button
              size="xs"
              variant="ghost"
              colorScheme="teal"
              isLoading={fetchingCases}
              onClick={() => setCasesLimit((n) => n + PILOT_CASES_PAGE_SIZE)}
            >
              Charger plus de cas
            </Button>
          </Center>
        )}
      </Box>

      <HStack spacing={2}>
        <Button
          size="xs"
          variant="outline"
          leftIcon={<Icon as={LuExternalLink} boxSize={3} />}
          onClick={() => navigate(`/admin/users/${userId}`)}
        >
          Gérer les modules
        </Button>
      </HStack>
    </VStack>
  )
}
